import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { HttpError } from "@/api/client";
import { useScope } from "@/api/queries";
import { useScopeAuthGeneration } from "@/api/ScopeAuthLifecycle";
import { useExtensionCapabilitySnapshot } from "@/api/capabilities";
import { enabledExtensionIds } from "@/app/extensions/registry";
import { useDashboardStore } from "@/state/dashboardStore";
import { NamespaceMultiSelector } from "@/components/NamespaceMultiSelector";
import { embeddedDashboards } from "@/generated/dashboards";
import { useAuth } from "@/app/AuthGate";
import { StreamInvalidator } from "@/app/StreamInvalidator";
import type { SSETransportState } from "@/api/sse";
import { DiagnoseGlyph, ObserverGlyph } from "@/components/BrandIcons";
import { CommandPalette } from "@/app/CommandPalette";
import { NAV_GROUPS, navRoutesInGroup, type RouteCapabilities } from "@/app/routes";
import { useAsyncErrorFocus, useRouteFocus } from "@/app/focus";
import { useT } from "@/i18n/I18nProvider";
import { LOCALE_QUERY_PARAM, isSupportedLocale } from "@/i18n/locales";
import { LocaleSelector } from "@/i18n/LocaleSelector";
import { useRouteLabel } from "@/i18n/routeLabel";
import { buildVersion } from "@/lib/buildVersion";
import { usingMockApi } from "@/lib/env";
import { DiagnosePanel } from "@/features/diagnose/DiagnosePanel";
import { useDiagnoseUI } from "@/features/diagnose/panelState";
import { isLegacyDetailRoute, routeFocus } from "@/features/diagnose/routeFocus";
import { useViewerShare } from "@/features/diagnose/useViewerShare";
import {
  activatePreferencePartition,
  isPreferencePartitionFenced,
  readNavigationPreference,
  readNamespaceSelectionPreference,
  resumePreferencePartition,
  watchPreferencePartition,
  writeNavigationPreference,
  writeNamespaceSelectionPreference,
  type NavigationPreference,
} from "@/app/preference-storage";
import { moveVisibleRoute, orderVisibleRoutes, shortcutRoutes } from "@/app/navigation-preferences";
import { useNavigationShortcuts } from "@/app/useNavigationShortcuts";
import { FavoritesDisclosure } from "@/app/FavoritesDisclosure";
import { ThemeSelector } from "@/app/ThemeSelector";
import {
  canonicalNamespaceSelection,
  namespaceSelectionTransport,
  scopeFingerprint,
  selectionAuthorized,
  type NamespaceSelection,
} from "@/state/namespaceSelection";
import { NamespaceSelectionApplyContext, NamespaceSelectionIdentityContext } from "@/state/namespaceSelectionIdentity";

/* 화면을 넘어가도 유지할 공용 파라미터 — 시간 범위와 표시 언어뿐입니다.
   클러스터·전역 namespace는 URL이 아니라 앱 상태라 여기 없습니다. (#94)
   from/to·edge·uid·workload·container·levels·q·tab 같은 **화면 전용 필터**는
   nav로 다른 화면에 새어들어가면 안 됩니다. 예전에는 search를 통째로 넘겨
   Topology의 edge나 로그 차트 구간(from/to)이 Logs Explorer로 흘러들어가
   빈 결과("데이터 없음")와 지저분한 URL을 만들었습니다. (#31 후속) */
/* 표시 언어도 공용입니다 (ADR 0025). 빠뜨리면 nav·팔레트로 이동하는 순간 URL에서
   `lang`이 사라지고, 저장된 선택이 없는 첫 전환에서는 브라우저 언어로 되돌아갑니다. */
const SHARED_PARAMS = ["range", "refresh", LOCALE_QUERY_PARAM];
/* 기본값과 같으면 URL에서 생략합니다 — 링크 하나로 재현되는 것은 그대로면서 URL이 짧아집니다. */
const PARAM_DEFAULTS: Record<string, string> = { range: "1h", refresh: "30000" };
const TRANSPORT_MESSAGE: Record<SSETransportState, string> = {
  connecting: "nav.stream.connecting",
  connected: "nav.stream.connected",
  reconnecting: "nav.stream.reconnecting",
  failed: "nav.stream.failed",
};

export function sharedSearch(search: string): string {
  const src = new URLSearchParams(search);
  const out = new URLSearchParams();
  for (const k of SHARED_PARAMS) {
    const v = src.get(k);
    if (!v || v === PARAM_DEFAULTS[k]) continue;
    /* 지원하지 않는 언어는 전파하지 않습니다 — `?lang=ja`가 화면을 옮겨 다니며
       살아남으면 링크를 공유할 때마다 없는 언어가 따라다닙니다. */
    if (k === LOCALE_QUERY_PARAM && !isSupportedLocale(v)) continue;
    out.set(k, v);
  }
  const s = out.toString();
  return s ? `?${s}` : "";
}

/** 좌측 내비게이션은 현재 시간 범위만 유지한 채 이동합니다. */
export function AppShell() {
  const { pathname, search } = useLocation();
  const navSearch = useMemo(() => sharedSearch(search), [search]);
  const scope = useScope();
  const canManage = scope.data?.canManageWorkloads ?? false;
  /* Resources 진입점은 서버가 준 capability로만 노출합니다 — 관리 그룹 조건과 별개입니다.
     서버는 platform.admin(또는 AUTH_MODE=none)이고 direct 모드 서비스가 있을 때만 true를 줍니다. (ADR 0018) */
  const canExplore = scope.data?.canExploreResources ?? false;
  /* Debug Sessions 관리 화면은 platform.admin 파생 + 세션 레지스트리 활성일 때만 열립니다. (ADR 0030) */
  const canManageExecSessions = scope.data?.canManageExecSessions ?? false;
  const canViewGitOps = scope.data?.canViewGitOps ?? false;
  /* 라우트 레지스트리와 팔레트가 함께 보는 capability 한 벌입니다.
     서버 응답이 아직 없으면 **닫힌 쪽**으로 답합니다. */
  /* 확장 기여(ADR 0024)는 서버가 등록했다고 답한 것만 nav·팔레트에 나옵니다.
     확인 전·확인 실패는 빈 집합이라 목적지가 생기지 않습니다(fail closed). */
  const { snapshot } = useExtensionCapabilitySnapshot();
  const enabledExtensions = useMemo(() => enabledExtensionIds(snapshot), [snapshot]);
  const caps: RouteCapabilities = useMemo(
    () => ({ canExploreResources: canExplore, canManageWorkloads: canManage, canManageExecSessions, canViewGitOps, enabledExtensions }),
    [canExplore, canManage, canManageExecSessions, canViewGitOps, enabledExtensions],
  );
  /* 진단은 화면 하나에 매인 기능이 아닙니다 — 패널은 셸에 한 번 붙고, 공유가
     켜져 있는 동안 라우트가 바뀔 때마다 지금 화면을 상류에 등록합니다. (ADR-0005) */
  const diagnose = useDiagnoseUI();
  const scopeAuthGeneration = useScopeAuthGeneration();
  const clusterId = useDashboardStore((s) => s.clusterId);
  const namespace = useDashboardStore((s) => s.namespace);
  const namespaceSelection = useDashboardStore((s) => s.namespaceSelection);
  const namespaceSelectionStatus = useDashboardStore((s) => s.namespaceSelectionStatus);
  const setClusterId = useDashboardStore((s) => s.setClusterId);
  const setNamespaceSelectionContext = useDashboardStore((s) => s.setNamespaceSelectionContext);
  const commitNamespaceSelection = useDashboardStore((s) => s.commitNamespaceSelection);
  const retryNamespaceSelection = useDashboardStore((s) => s.retryNamespaceSelection);
  const focus = useMemo(
    () => routeFocus(pathname, search, { clusterId, namespace }),
    [pathname, search, clusterId, namespace],
  );
  useViewerShare(diagnose.share, focus);
  const auth = useAuth();
  const [, setParams] = useSearchParams();
  const { t } = useT("nav");
  const routeLabel = useRouteLabel();
  const navigate = useNavigate();
  const scopeVerified = Boolean(scope.data && !scope.isFetching && !scope.isError);
  const preferencePartition = scopeVerified ? scope.data?.preferencePartition ?? "" : "";
  const [activePreferencePartition, setActivePreferencePartition] = useState<string>();
  const [preferenceEpoch, setPreferenceEpoch] = useState(0);
  const fencedPartitionRef = useRef<string>();
  const suspendedPartitionRef = useRef<string>();
  if (!scopeVerified && activePreferencePartition) {
    suspendedPartitionRef.current = activePreferencePartition;
    setActivePreferencePartition(undefined);
  }
  useEffect(() => {
    if (!scopeVerified) return undefined;
    if (!preferencePartition) {
      if (activePreferencePartition) suspendedPartitionRef.current = activePreferencePartition;
      setActivePreferencePartition(undefined);
      return undefined;
    }
    if (fencedPartitionRef.current === preferencePartition) return undefined;
    fencedPartitionRef.current = undefined;
    const resuming = suspendedPartitionRef.current === preferencePartition;
    if (!resuming) suspendedPartitionRef.current = undefined;
    const activated = resuming
      ? resumePreferencePartition(preferencePartition)
      : activatePreferencePartition(preferencePartition);
    if (activated) suspendedPartitionRef.current = undefined;
    setActivePreferencePartition(activated ? preferencePartition : undefined);
    if (!activated) return undefined;
    return watchPreferencePartition(preferencePartition, () => {
      fencedPartitionRef.current = preferencePartition;
      suspendedPartitionRef.current = preferencePartition;
      setActivePreferencePartition(undefined);
      setPreferenceEpoch((value) => value + 1);
    });
  }, [activePreferencePartition, preferencePartition, scope.dataUpdatedAt, scopeVerified]);
  const preferenceReady = scopeVerified && activePreferencePartition === preferencePartition;
  const namespaceIdentityReady = scopeVerified && Boolean(preferencePartition) &&
    !isPreferencePartitionFenced(preferencePartition) && fencedPartitionRef.current !== preferencePartition;
  const favoritesGeneration = `${scopeAuthGeneration}|${scope.dataUpdatedAt ?? 0}|${preferenceEpoch}|${scopeFingerprint(scopeVerified ? scope.data : undefined, clusterId)}|${canExplore ? 1 : 0}`;
  const [navigationPreference, setNavigationPreference] = useState<NavigationPreference>({ groups: {}, shortcutsEnabled: false });
  useEffect(() => {
    setNavigationPreference(preferenceReady
      ? readNavigationPreference(preferencePartition) ?? { groups: {}, shortcutsEnabled: false }
      : { groups: {}, shortcutsEnabled: false });
  }, [preferenceEpoch, preferencePartition, preferenceReady]);
  const effectiveNavigationPreference = preferenceReady
    ? navigationPreference
    : { groups: {}, shortcutsEnabled: false } satisfies NavigationPreference;
  const visibleByGroup = useMemo(() => new Map(
    NAV_GROUPS.map((group) => [group.id, navRoutesInGroup(group.id, caps)] as const),
  ), [caps]);
  const numberedRoutes = useMemo(
    () => shortcutRoutes(effectiveNavigationPreference.groups, visibleByGroup),
    [effectiveNavigationPreference.groups, visibleByGroup],
  );
  const shortcutNavigate = useCallback((path: string) => {
    navigate({ pathname: path, search: navSearch });
  }, [navSearch, navigate]);
  const shortcutIdentity = `${favoritesGeneration}|${preferencePartition}|${clusterId}|${pathname}|${numberedRoutes.map((route) => route.id).join(",")}`;
  useNavigationShortcuts(
    preferenceReady && effectiveNavigationPreference.shortcutsEnabled,
    numberedRoutes,
    shortcutNavigate,
    shortcutIdentity,
  );
  const persistNavigation = useCallback((next: NavigationPreference) => {
    if (!preferenceReady || !writeNavigationPreference(preferencePartition, next)) return false;
    setNavigationPreference(next);
    return true;
  }, [preferencePartition, preferenceReady]);
  const [transportState, setTransportState] = useState<SSETransportState>();
  /* 라우트가 바뀔 때만 본문으로 포커스를 옮깁니다. 자동 갱신·필터 변경은
     쿼리만 바꾸므로 사용자가 있던 자리를 빼앗지 않습니다. (ADR 0025) */
  const mainRef = useRouteFocus<HTMLElement>();
  /* 클러스터 목록 조회 실패는 화면 전체가 멈추는 비동기 오류입니다. 처음 나타난
     순간에만 알림으로 포커스를 옮깁니다 — 재시도마다 옮기면 자리를 잃습니다. */
  const scopeAlertRef = useRef<HTMLDivElement>(null);
  useAsyncErrorFocus(scope.isError ? "scope-error" : null, scopeAlertRef);

  /* Observer는 단일 클러스터 배포라 Scope의 첫 접근 가능 클러스터를 사용합니다. */
  useEffect(() => {
    if (!scope.data) return;
    const first = scope.data.clusters.find((c) => c.accessible) ?? scope.data.clusters[0];
    if (!first) return;
    setClusterId(first.id);
  }, [scope.data, setClusterId]);

  /* 옛 cluster와 목록의 ns 쿼리를 전역 선택으로 채택하지 않고 지웁니다.
     옛 상세 redirect만 새 path를 만들 때까지 ns를 보존합니다. */
  useEffect(() => {
    const params = new URLSearchParams(search);
    const legacyDetail = isLegacyDetailRoute(pathname);
    if (!params.has("cluster") && (legacyDetail || !params.has("ns"))) return;
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.delete("cluster");
        if (!legacyDetail) p.delete("ns");
        return p;
      },
      { replace: true },
    );
  }, [pathname, search, setParams]);

  const namespaceIdentityRef = useRef("");
  const fingerprint = scopeFingerprint(scopeVerified ? scope.data : undefined, clusterId);
  useEffect(() => {
    const currentScope = scope.data;
    if (!namespaceIdentityReady || !clusterId || !preferencePartition || !currentScope) {
      if (scopeVerified && (!preferencePartition || isPreferencePartitionFenced(preferencePartition) ||
          fencedPartitionRef.current === preferencePartition)) {
        namespaceIdentityRef.current = "";
        setNamespaceSelectionContext(namespaceSelection, "repair", currentScope?.namespaceSelection);
      } else if (namespaceSelection && namespaceSelectionStatus !== "ack-lost") {
        setNamespaceSelectionContext(namespaceSelection, "pending");
      }
      return;
    }
    const identityKey = `${preferencePartition.length}:${preferencePartition}|${clusterId.length}:${clusterId}|${fingerprint}`;
    if (namespaceIdentityRef.current === identityKey && namespaceSelection && selectionAuthorized(currentScope, clusterId, namespaceSelection)) {
      if (namespaceSelectionStatus === "pending") {
        setNamespaceSelectionContext(namespaceSelection, "ready", currentScope.namespaceSelection);
      } else {
        setNamespaceSelectionContext(namespaceSelection, namespaceSelectionStatus, currentScope.namespaceSelection);
      }
      return;
    }
    namespaceIdentityRef.current = identityKey;
    const identity = { partition: preferencePartition, clusterId, scopeFingerprint: fingerprint };
    if (!preferenceReady) {
      setNamespaceSelectionContext(undefined, "repair", currentScope.namespaceSelection);
      return;
    }
    const stored = readNamespaceSelectionPreference(identity);
    if (stored.status === "valid" && selectionAuthorized(currentScope, clusterId, stored.selection)) {
      setNamespaceSelectionContext(stored.selection, "ready", currentScope.namespaceSelection);
      return;
    }
    if (stored.status === "absent" && !stored.legacyPresent) {
      const initial = { mode: "all" } as const;
      writeNamespaceSelectionPreference(identity, initial);
      setNamespaceSelectionContext(initial, "ready", currentScope.namespaceSelection);
      return;
    }
    setNamespaceSelectionContext(undefined, "repair", currentScope.namespaceSelection);
  }, [clusterId, fingerprint, namespaceIdentityReady, namespaceSelection, namespaceSelectionStatus, preferencePartition, preferenceReady, scope.data, setNamespaceSelectionContext]);

  const applyNamespaceSelection = useCallback((selection: NamespaceSelection) => {
    const currentScope = scope.data;
    const canonical = canonicalNamespaceSelection(selection);
    if (!namespaceIdentityReady || !clusterId || !preferencePartition ||
        !currentScope || !canonical || !selectionAuthorized(currentScope, clusterId, canonical)) return false;
    if (preferenceReady) {
      writeNamespaceSelectionPreference({ partition: preferencePartition, clusterId, scopeFingerprint: fingerprint }, canonical);
    }
    /* 저장 중 다른 탭이 marker를 바꾸면 옛 이벤트가 메모리 선택까지 덮지 못합니다. */
    if (isPreferencePartitionFenced(preferencePartition) || fencedPartitionRef.current === preferencePartition) return false;
    commitNamespaceSelection(canonical, currentScope.namespaceSelection);
    return true;
  }, [clusterId, commitNamespaceSelection, fingerprint, namespaceIdentityReady, preferencePartition, preferenceReady, scope.data]);
  const selectionTransport = namespaceSelection
    ? namespaceSelectionTransport(namespaceSelection, scope.data?.namespaceSelection)
    : undefined;
  const capabilityBlocked = namespaceSelectionStatus === "ready" && selectionTransport?.mode === "blocked";
  const selectedCluster = scope.data?.clusters.find((candidate) => candidate.id === clusterId);
  const verifiedClusterDenied = scopeVerified && Boolean(clusterId) && !selectedCluster?.accessible;

  /* 관리자 전용 배포(ADR 0035)에서 필수 역할이 없는 사용자입니다. 재로그인으로 풀리지 않으므로
     앱 셸 대신 이유와 로그아웃만 보입니다 — nav·화면이 403 조각으로 깨진 모습을 만들지 않습니다. */
  if (scope.error instanceof HttpError && scope.error.body.code === "role_required") {
    return (
      <main className="app-gate" id="main" data-testid="role-required">
        <div className="state" role="alert">
          <span className="state__title">{t("nav.roleRequired.title")}</span>
          <span className="muted">{t("nav.roleRequired.detail")}</span>
          {auth.enabled && (
            <button type="button" className="ds-button ds-button--outline ds-button--sm" onClick={() => void auth.logout()}>
              {t("nav.signOut")}
            </button>
          )}
        </div>
      </main>
    );
  }

  return (
    <div className="app">
      <StreamInvalidator
        clusterId={auth.enabled && namespaceIdentityReady && !verifiedClusterDenied ? clusterId : undefined}
        onTransportState={setTransportState}
      />
      <a className="skip-link" href="#main">
        {t("nav.skipLink")}
      </a>

      <div className="app__brand">
        <span className="ds-brand-mark" aria-hidden="true">
          <ObserverGlyph />
        </span>
        {t("nav.brand")}
      </div>

      <header className="app__topbar">
        {/* 전역 namespace 선택 — 모든 화면 공용입니다. 클러스터는 단일 배포라 고르지 않습니다. (#94) */}
        <NamespaceMultiSelector
          scope={scope.data}
          clusterId={clusterId}
          selection={namespaceSelection}
          status={capabilityBlocked ? "ack-lost" : namespaceSelectionStatus}
          onApply={applyNamespaceSelection}
          onRetry={() => { if (capabilityBlocked) void scope.refetch(); else retryNamespaceSelection(); }}
          disabled={scope.isLoading || !namespaceIdentityReady}
          labels={{
            label: t("nav.namespace.label"),
            all: t("nav.namespace.all"),
            apply: t("nav.namespace.apply"),
            repair: t("nav.namespace.repair"),
            acknowledgement: capabilityBlocked ? t("nav.namespace.capability") : t("nav.namespace.acknowledgement"),
            retry: t("nav.namespace.retry"),
            selected: t("nav.namespace.selected"),
            limit: t("nav.namespace.limit"),
          }}
        />
        {usingMockApi && (
          <span className="muted" style={{ font: "var(--type-meta)" }}>
            {t("nav.mockApi")}
          </span>
        )}
        {auth.enabled && transportState && (
          <span className={`stream-transport stream-transport--${transportState}`}>
            <span aria-hidden="true">●</span> {t(TRANSPORT_MESSAGE[transportState])}
          </span>
        )}
        <ThemeSelector />
        <LocaleSelector />
		{auth.enabled && auth.session?.authenticated && <span className="auth-user">{auth.session.principal.displayName}<button type="button" className="ds-button ds-button--ghost ds-button--sm" data-testid="sign-out" onClick={() => void auth.logout()}>{t("nav.signOut")}</button></span>}
      </header>

      <nav className="app__nav" aria-label={t("nav.label")}>
        {preferenceReady && canExplore && clusterId && (
          <FavoritesDisclosure
            key={`${preferencePartition}|${clusterId}|${favoritesGeneration}`}
            partition={preferencePartition}
            clusterId={clusterId}
            navSearch={navSearch}
            generation={favoritesGeneration}
          />
        )}
        {preferenceReady && (
          <details className="app__nav-settings">
            <summary>{t("nav.settings.title")}</summary>
            <label>
              <input
                type="checkbox"
                checked={effectiveNavigationPreference.shortcutsEnabled}
                onChange={(event) => persistNavigation({ ...navigationPreference, shortcutsEnabled: event.target.checked })}
              />
              {t("nav.shortcuts.toggle")}
            </label>
            <p className="muted">{t("nav.shortcuts.help")}</p>
          </details>
        )}
        {/* 목적지는 라우트 레지스트리 하나에서 옵니다 — 라우터·nav·팔레트가 같은
            배열을 읽으므로 "nav에는 있는데 라우터에는 없는 경로"가 생기지 않습니다.
            그룹 순서·제목·노출 조건은 기존 그대로입니다. */}
        {NAV_GROUPS.map((group) => {
          const visible = visibleByGroup.get(group.id) ?? [];
          const entries = orderVisibleRoutes(group.id, visible, effectiveNavigationPreference.groups);
          const dashboards = group.id === "custom" ? embeddedDashboards : [];
          if (entries.length === 0 && dashboards.length === 0) return null;
          return (
            <div className="app__nav-group" key={group.id}>
              <div className="app__nav-title">{t(group.titleId)}</div>
              {/* Git에서 발견한 embedded dashboard는 개수가 배포마다 달라 레지스트리에
                  담을 수 없습니다. 표시 순서는 기존과 같이 dashboard 먼저입니다. */}
              {dashboards.map((dashboard) => (
                <NavLink
                  key={dashboard.id}
                  to={{ pathname: `/dashboards/${dashboard.id}`, search: navSearch }}
                  className="app__nav-link"
                >
                  {dashboard.title}
                </NavLink>
              ))}
              {entries.map((route, index) => (
                <div className="app__nav-row" key={route.id}>
                  <NavLink to={{ pathname: route.path, search: navSearch }} end={route.end} className="app__nav-link">
                    {routeLabel(route)}
                    {effectiveNavigationPreference.shortcutsEnabled && numberedRoutes.findIndex((item) => item.id === route.id) >= 0 ? (
                      <kbd>{t("nav.shortcuts.hint", { number: numberedRoutes.findIndex((item) => item.id === route.id) + 1 })}</kbd>
                    ) : null}
                  </NavLink>
                  {preferenceReady && (
                    <span className="app__nav-order">
                      <button type="button" className="linkish" disabled={index === 0} aria-label={t("nav.order.up", { name: routeLabel(route) })} onClick={(event) => {
                        const link = event.currentTarget.closest(".app__nav-row")?.querySelector<HTMLAnchorElement>(".app__nav-link");
                        if (persistNavigation({ ...navigationPreference, groups: moveVisibleRoute(group.id, visible, navigationPreference.groups, route.id, -1) })) link?.focus();
                      }}>↑</button>
                      <button type="button" className="linkish" disabled={index === entries.length - 1} aria-label={t("nav.order.down", { name: routeLabel(route) })} onClick={(event) => {
                        const link = event.currentTarget.closest(".app__nav-row")?.querySelector<HTMLAnchorElement>(".app__nav-link");
                        if (persistNavigation({ ...navigationPreference, groups: moveVisibleRoute(group.id, visible, navigationPreference.groups, route.id, 1) })) link?.focus();
                      }}>↓</button>
                    </span>
                  )}
                </div>
              ))}
            </div>
          );
        })}
        {/* 지금 떠 있는 발행본. 날짜 태그·sha는 레지스트리 태그와 그대로 대조됩니다. (#97) */}
        <p className="app__version num" title={t("nav.buildVersion")}>
          {buildVersion}
        </p>
      </nav>

      {/* 팔레트는 셸에 한 번만 붙습니다. 화면마다 붙이면 단축키가 여러 번 걸립니다. */}
      <CommandPalette clusterId={clusterId} caps={caps} navSearch={navSearch} />

      {/* tabIndex=-1은 프로그램 포커스 전용입니다 — Tab 순서에 본문이 끼어들지
          않으면서 라우트 변경·건너뛰기 링크의 착지점이 됩니다. */}
      <main className="app__main" id="main" ref={mainRef} tabIndex={-1}>
        {clusterId && !verifiedClusterDenied ? (
          <NamespaceSelectionIdentityContext.Provider value={namespaceIdentityReady}>
            <NamespaceSelectionApplyContext.Provider value={applyNamespaceSelection}>
              <Outlet />
            </NamespaceSelectionApplyContext.Provider>
          </NamespaceSelectionIdentityContext.Provider>
        ) : scope.isError ? (
          <div className="state" role="alert" tabIndex={-1} ref={scopeAlertRef}>
            <span className="state__title">{t("nav.main.error.title")}</span>
            <span className="muted">{t("nav.main.error.detail")}</span>
          </div>
        ) : verifiedClusterDenied ? (
          <div className="state" role="alert">
            <span className="state__title">{t("nav.main.forbidden.title")}</span>
            <span className="muted">{t("nav.main.forbidden.detail")}</span>
          </div>
        ) : (
          <div className="state" aria-busy="true">
            <span className="state__title">{t("nav.main.loading")}</span>
          </div>
        )}
      </main>

      <button
        type="button"
        className="diagnose-fab"
        aria-pressed={diagnose.open}
        aria-controls={diagnose.open ? "diagnose-panel" : undefined}
        aria-label={t("nav.diagnose.toggle")}
        onClick={() => diagnose.setOpen(!diagnose.open)}
      >
        <DiagnoseGlyph />
      </button>

      {/* 패널은 셸에 한 번만 붙습니다 — 화면마다 붙이면 대화가 라우트마다 끊깁니다. */}
      {/* 공유 토글은 패널 안에 있지만 상태는 셸이 갖습니다 — 패널을 닫아도 공유는
          유지되고, 위젯은 이 대시보드의 상태 저장 방식을 알지 않습니다. */}
      <DiagnosePanel
        open={diagnose.open}
        onClose={() => diagnose.setOpen(false)}
        namespace={focus?.namespace}
        share={{ label: t("nav.diagnose.share"), checked: diagnose.share, onChange: diagnose.setShare }}
        /* 등록과 같은 값이며 공유를 끄면 둘 다 나가지 않습니다 — 질문에 실리는 사본은
           등록이 비었을 때(재시작·복제본·이동 직후)만 상류가 읽습니다. (ADR-0005 §2) */
        focus={diagnose.share ? focus : null}
      />
    </div>
  );
}
